import { api } from '@packages/backend/convex/_generated/api';
import {
  elapsedTime,
  formatCents,
  getLinklyDeviceLinklyEnv,
  getUserErrorMessage,
  type LinklyEnv,
} from '@packages/shared';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { ErrorAlert } from '@/components/ErrorAlert';
import { SessionStatusBadge } from '@/components/SessionStatusBadge';
import { TimeDisplay } from '@/components/TimeDisplay';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { LinklyDeviceConnectionStatus } from './components/LinklyDeviceConnectionStatus';

export const Route = createFileRoute('/a/linkly/devices/$id/')({
  staticData: {
    breadcrumbs: [{ label: 'Linkly Devices', url: '/a/linkly/devices' }, { label: 'Details' }],
  },
  component: Component,
});

const ENV_HELPER_TEXT: Record<LinklyEnv, string> = {
  emulator: 'Routes to the OrderHub Linkly emulator. No real device or card.',
  sandbox: 'Routes to Linkly sandbox. Pairs Linkly Windows VPP. No real cards.',
  production: 'Routes to Linkly production. Pairs a physical EFTPOS terminal. Real cards.',
};

// A local Convex deployment isn't publicly reachable, so can't be reached by Linkly cloud.
// "sandbox" and "production" only work with a Convex cloud deployment.
const convexUrl = import.meta.env.VITE_CONVEX_URL;
const IS_LOCAL_CONVEX =
  !!convexUrl && (convexUrl.includes('127.0.0.1') || convexUrl.includes('localhost'));

const DISABLED_ENV_TOOLTIP = 'Needs a public callback URL, so unavailable on local Convex.';

const EnvToggleItem = ({
  value,
  label,
  disabled,
}: {
  value: LinklyEnv;
  label: string;
  disabled: boolean;
}) => {
  const item = (
    <ToggleGroupItem
      className="px-3 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
      value={value}
      aria-disabled={disabled || undefined}
    >
      {label}
    </ToggleGroupItem>
  );

  if (!disabled) return item;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      <TooltipContent>{DISABLED_ENV_TOOLTIP}</TooltipContent>
    </Tooltip>
  );
};

function Component() {
  const { id } = Route.useParams();
  const router = useRouter();
  const { data, error, isError, isPending } = useQuery(api.linklyDevices.listAll);
  const { data: sessions } = useQuery(api.linklySessions.listByDevice, {
    linklyDeviceId: id,
  });
  const [pairDialogOpen, setPairDialogOpen] = useState(false);
  const [pairCode, setPairCode] = useState('');
  const convex = useConvex();

  const handleRefreshToken = async () => {
    try {
      await convex.mutation(api.linklyDevices.refreshToken, {
        linklyDeviceId: id,
      });
      toast.success('Token refresh initiated');
    } catch (error) {
      toast.error('Failed to refresh token', { description: getUserErrorMessage(error) });
    }
  };

  const handlePairAgain = () => {
    setPairDialogOpen(true);
  };

  const handlePair = async () => {
    if (!pairCode.trim()) {
      toast.error('Please enter a pair code');
      return;
    }

    try {
      await convex.action(api.linklyDevices.rePairDeviceByLinklyDeviceId, {
        linklyDeviceId: device._id,
        pairCode: pairCode.trim(),
      });
      toast.success('Device re-pairing initiated');
      setPairDialogOpen(false);
      setPairCode('');
    } catch (error) {
      toast.error('Failed to re-pair device', { description: getUserErrorMessage(error) });
    }
  };

  const handleGetPinPadStatus = async (linklyDeviceId: string) => {
    try {
      const result = await convex.action(api.linklyDevices.getStatus, {
        linklyDeviceId,
      });
      toast.success('Pin pad status', {
        description: result?.response ? JSON.stringify(result.response, null, 2) : 'No result',
      });
    } catch (error) {
      toast.error('Error', { description: getUserErrorMessage(error) });
    }
  };

  const handleSetLinklyEnv = async (linklyEnv: LinklyEnv) => {
    try {
      await convex.mutation(api.linklyDevices.setLinklyEnv, { linklyDeviceId: id, linklyEnv });
      toast.success(
        linklyEnv === 'emulator'
          ? 'Switched to emulator — ready to use, no pairing needed'
          : `Switched to ${linklyEnv} — pair the device to finish`,
      );
    } catch (error) {
      toast.error('Failed to switch Linkly env', { description: getUserErrorMessage(error) });
    }
  };

  const handleClearCredentials = async () => {
    try {
      await convex.mutation(api.linklyDevices.clearCredentials, {
        linklyDeviceId: id,
      });
      toast.success('Credentials cleared successfully');
    } catch (error) {
      toast.error('Failed to clear credentials', { description: getUserErrorMessage(error) });
    }
  };

  if (isPending) return <LoadingIcon />;
  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading Linkly device details" />;
  }

  const selectedDeviceData = data?.find((d) => d.linklyDevice._id === id);
  if (!selectedDeviceData) return <div>Linkly device not found.</div>;

  const { linklyDevice: device, restaurantName, connectionStatus } = selectedDeviceData;

  return (
    <div>
      <div className="mb-4">
        <ActionButton asChild variant="outline" size="sm">
          <Link to="/a/linkly/devices">
            <ArrowLeft className="h-4 w-4" />
            Back to list
          </Link>
        </ActionButton>
      </div>

      {device.offlineAt && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>
            OFFLINE. Last seen approx{' '}
            {formatDistanceToNow(new Date(device.offlineAt), { addSuffix: true })}.
          </AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              Connection has been lost to the EFTPOS device. It may have been:
              <br /> - Unpaired by the user
              <br /> - Lost internet
              <br /> - Powered off
            </span>
            <ActionButton size="sm" variant="outline" onClick={handlePairAgain}>
              Pair again
            </ActionButton>
          </AlertDescription>
        </Alert>
      )}

      <Card className="mb-6">
        <CardHeader>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <CardTitle>Linkly Device</CardTitle>
              {import.meta.env.VITE_STAGE !== 'production' && (
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  value={getLinklyDeviceLinklyEnv(device)}
                  onValueChange={(value) => {
                    if (value !== 'emulator' && value !== 'sandbox' && value !== 'production')
                      return;
                    if (IS_LOCAL_CONVEX && value !== 'emulator') return;
                    handleSetLinklyEnv(value);
                  }}
                >
                  <EnvToggleItem value="emulator" label="Emulator" disabled={false} />
                  <EnvToggleItem value="sandbox" label="Sandbox" disabled={IS_LOCAL_CONVEX} />
                  <EnvToggleItem value="production" label="Production" disabled={IS_LOCAL_CONVEX} />
                </ToggleGroup>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {ENV_HELPER_TEXT[getLinklyDeviceLinklyEnv(device)]}
            </p>
          </div>
          <CardAction>
            <ActionDropdown>
              <DropdownMenuItem onSelect={handleRefreshToken}>Refresh Token</DropdownMenuItem>
              <DropdownMenuItem onSelect={handlePairAgain}>Pair again</DropdownMenuItem>

              <DropdownMenuItemDestructive onSelect={handleClearCredentials}>
                Clear credentials
              </DropdownMenuItemDestructive>

              <Separator />
              <DropdownMenuItem
                onSelect={() =>
                  router.navigate({
                    to: '/a/linkly/devices/$id/virtual-pin-pad',
                    params: { id },
                  })
                }
              >
                View emulator
              </DropdownMenuItem>
            </ActionDropdown>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">Property</TableHead>
                <TableHead>Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">Restaurant</TableCell>
                <TableCell>{restaurantName ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Username</TableCell>
                <TableCell>
                  {device.username} / {device.password}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">POS Terminal UUID</TableCell>
                <TableCell>{device.posTerminalUuid ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Status</TableCell>
                <TableCell>
                  <LinklyDeviceConnectionStatus connectionStatus={connectionStatus} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Token expires</TableCell>
                <TableCell>
                  <TimeDisplay timestamp={device.tokenExpiresAt} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Last Updated</TableCell>
                <TableCell>
                  <TimeDisplay timestamp={device.updatedAt} showTime={false} />
                </TableCell>
              </TableRow>
              {device.secretGeneratedAt && (
                <TableRow>
                  <TableCell className="font-medium">Paired</TableCell>
                  <TableCell>
                    <TimeDisplay timestamp={device.secretGeneratedAt} showTime={false} />
                  </TableCell>
                </TableRow>
              )}
              {device.tokenGenerationError && (
                <TableRow>
                  <TableCell className="font-medium">Token Generation Error</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-red-600">{device.tokenGenerationError}</div>
                      {device.tokenGenerationErrorAt && (
                        <div className="text-sm text-muted-foreground">
                          <TimeDisplay timestamp={device.tokenGenerationErrorAt} showTime={true} />
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {device.secretGenerationError && (
                <TableRow>
                  <TableCell className="font-medium">Secret Generation Error</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-red-600">{device.secretGenerationError}</div>
                      {device.secretGenerationErrorAt && (
                        <div className="text-sm text-muted-foreground">
                          <TimeDisplay timestamp={device.secretGenerationErrorAt} showTime={true} />
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Status</CardTitle>
          <CardAction>
            <ActionButton variant="outline" size="sm" onClick={() => handleGetPinPadStatus(id)}>
              Get status
            </ActionButton>
          </CardAction>
        </CardHeader>
        <CardContent>
          {device.status && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/3">Property</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {device.status.bankDescription && (
                  <TableRow>
                    <TableCell className="font-medium">Bank Description</TableCell>
                    <TableCell>{device.status.bankDescription}</TableCell>
                  </TableRow>
                )}
                {device.status.timeout !== null && (
                  <TableRow>
                    <TableCell className="font-medium">Timeout</TableCell>
                    <TableCell>{device.status.timeout}ms</TableCell>
                  </TableRow>
                )}
                {device.status.pinPadSerialNumber && (
                  <TableRow>
                    <TableCell className="font-medium">Pin Pad Serial Number</TableCell>
                    <TableCell>{device.status.pinPadSerialNumber}</TableCell>
                  </TableRow>
                )}
                {device.status.pinPadVersion && (
                  <TableRow>
                    <TableCell className="font-medium">Pin Pad Version</TableCell>
                    <TableCell>{device.status.pinPadVersion}</TableCell>
                  </TableRow>
                )}
                {device.status.eftTerminalType && (
                  <TableRow>
                    <TableCell className="font-medium">EFT Terminal Type</TableCell>
                    <TableCell>{device.status.eftTerminalType}</TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell className="font-medium">Status Last Updated</TableCell>
                  <TableCell>
                    <TimeDisplay timestamp={device.status.updatedAt} showTime={true} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[220px]">Created</TableHead>
                <TableHead>Session UUID</TableHead>
                <TableHead className="w-[90px]">Duration</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions?.map((session) => (
                <TableRow key={session._id} className="hover:bg-muted/50">
                  <TableCell>
                    <TimeDisplay timestamp={session._creationTime} showTime={true} />
                  </TableCell>
                  <TableCell className="font-mono text-sm w-[350px]">
                    <Link
                      to="/a/linkly/sessions/$sessionUuid"
                      params={{ sessionUuid: session.uuid }}
                      className="hover:underline"
                    >
                      {session.uuid}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono">
                    {elapsedTime(
                      { start: session._creationTime, end: session.resultAt },
                      { showMs: false },
                    )}
                  </TableCell>
                  <TableCell className="font-mono">
                    {formatCents(session.paymentAmount, '$')}
                  </TableCell>
                  <TableCell>
                    <SessionStatusBadge result={session.result} />
                  </TableCell>
                </TableRow>
              ))}
              {!sessions?.length && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No sessions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={pairDialogOpen} onOpenChange={setPairDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pair Device</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={pairCode}
              onChange={(e) => setPairCode(e.target.value)}
              placeholder="Pair code (from EFTPOS terminal)"
              className="w-full"
            />
          </div>
          <DialogFooter>
            <ActionButton variant="outline" onClick={() => setPairDialogOpen(false)}>
              Cancel
            </ActionButton>
            <ActionButton onClick={handlePair} disabled={!pairCode.trim()}>
              Pair
            </ActionButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
